import { Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ConfirmModal } from "@/components/ConfirmModal";
import { Modal } from "@/components/Modal";
import { estimateMeal } from "@/lib/estimateMeal";
import {
  type ChatMessage,
  type Ingredient,
  MEAL_METRICS,
  type MealMetric,
  type Recipe,
} from "@/lib/schema";
import { recipeFinishedGrams, recipePortions, sumGrams } from "./amount";
import { formatAmount, METRIC_LABELS } from "./metrics";
import { sumMetrics } from "./scoring";

// The Oppskrifter editor (docs/adr/0006): name, finished weight, portions
// and an AI-assisted ingredient list. No transcript is stored — the current
// list is the recipe's state, and it goes to the estimator as the baseline,
// so a session opened a month later picks up from the list rather than from
// a stale conversation.
interface Props {
  open: boolean;
  // The recipe being edited, or null when creating one.
  initial: Recipe | null;
  // Name and ingredients carried in from «Lagre som oppskrift».
  prefill?: { name: string; ingredients: Ingredient[] } | null;
  // Every recipe, for the name-clash check.
  recipes: Recipe[];
  foodClarifications?: string[];
  onSave: (recipe: Recipe) => void;
  onClose: () => void;
}

export function RecipeEditorModal({ open, ...rest }: Props) {
  if (!open) return null;
  return <EditorBody key={rest.initial?.id ?? "new"} {...rest} />;
}

function parsePositive(value: string): number {
  const trimmed = value.trim();
  if (trimmed.length === 0) return Number.NaN;
  const parsed = Number(trimmed.replace(",", "."));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : Number.NaN;
}

function parseWholePositive(value: string): number {
  const parsed = parsePositive(value);
  return Number.isInteger(parsed) ? parsed : Number.NaN;
}

function scaleMetrics(
  amounts: Record<MealMetric, number>,
  factor: number,
): Record<MealMetric, number> {
  const scaled = {} as Record<MealMetric, number>;
  for (const metric of MEAL_METRICS) scaled[metric] = amounts[metric] * factor;
  return scaled;
}

// Grams edits scale a row's metrics from its estimated baseline, so repeated
// edits never compound — same rule as the meal chat.
function scaleFromBaseline(baseline: Ingredient, grams: number): Ingredient {
  if (baseline.grams <= 0) return { ...baseline, grams };
  const factor = grams / baseline.grams;
  const scaled = { ...baseline, grams };
  for (const metric of MEAL_METRICS) scaled[metric] = baseline[metric] * factor;
  return scaled;
}

function EditorBody({
  initial,
  prefill,
  recipes,
  foodClarifications,
  onSave,
  onClose,
}: Omit<Props, "open">) {
  const startingIngredients =
    initial?.ingredients ?? prefill?.ingredients ?? [];

  const [name, setName] = useState(initial?.name ?? prefill?.name ?? "");
  const [ingredients, setIngredients] =
    useState<Ingredient[]>(startingIngredients);
  const [baselines, setBaselines] = useState<Ingredient[]>(startingIngredients);
  const [gramsInputs, setGramsInputs] = useState<string[]>(() =>
    startingIngredients.map((ing) => formatAmount(ing.grams)),
  );

  // The finished weight tracks the ingredient sum until the user states one
  // — a dish that neither reduced nor gained is already correct, and the
  // number stops moving the moment they take ownership of it.
  const [finishedTouched, setFinishedTouched] = useState(
    initial ? initial.finishedGrams !== undefined : false,
  );
  const [finishedInput, setFinishedInput] = useState(
    formatAmount(
      initial ? recipeFinishedGrams(initial) : sumGrams(startingIngredients),
    ),
  );
  const [portionsInput, setPortionsInput] = useState(
    String(initial ? recipePortions(initial) : 1),
  );

  // Local to this session only, never persisted on the recipe.
  const [transcript, setTranscript] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<Recipe | null>(null);

  const chatLogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = chatLogRef.current;
    if (el && transcript.length > 0) el.scrollTop = el.scrollHeight;
  }, [transcript]);

  const rawGrams = sumGrams(ingredients);
  useEffect(() => {
    if (!finishedTouched) setFinishedInput(formatAmount(rawGrams));
  }, [rawGrams, finishedTouched]);

  const totals = sumMetrics(ingredients);
  const finishedGrams = parsePositive(finishedInput);
  const portions = parseWholePositive(portionsInput);
  const finishedValid = Number.isFinite(finishedGrams);
  const portionsValid = Number.isFinite(portions);

  const canSave =
    name.trim().length > 0 &&
    ingredients.length > 0 &&
    finishedValid &&
    portionsValid;

  const send = async () => {
    const text = draft.trim();
    if (text.length === 0 || sending) return;
    const nextTranscript: ChatMessage[] = [
      ...transcript,
      { role: "user", text },
    ];
    setSending(true);
    setChatError(null);
    try {
      const estimate = await estimateMeal({
        transcript: nextTranscript,
        previousEstimate:
          ingredients.length > 0
            ? { name: name.trim(), ingredients }
            : undefined,
        foodClarifications:
          foodClarifications && foodClarifications.length > 0
            ? foodClarifications
            : undefined,
      });
      setIngredients(estimate.ingredients);
      setBaselines(estimate.ingredients);
      setGramsInputs(
        estimate.ingredients.map((ing) => formatAmount(ing.grams)),
      );
      setTranscript([
        ...nextTranscript,
        { role: "assistant", text: estimate.comment },
      ]);
      setName((prev) => (prev.trim().length === 0 ? estimate.name : prev));
      // Only ever set when the user stated them outright.
      if (estimate.finishedGrams !== undefined) {
        setFinishedInput(formatAmount(estimate.finishedGrams));
        setFinishedTouched(true);
      }
      if (estimate.portions !== undefined) {
        setPortionsInput(String(estimate.portions));
      }
      setDraft("");
    } catch (err) {
      setChatError(
        err instanceof Error ? err.message : "Estimering feilet. Prøv igjen.",
      );
    } finally {
      setSending(false);
    }
  };

  const changeGrams = (index: number, value: string) => {
    setGramsInputs((prev) => prev.map((g, i) => (i === index ? value : g)));
    const trimmed = value.trim();
    const grams =
      trimmed.length === 0 ? Number.NaN : Number(trimmed.replace(",", "."));
    if (!Number.isFinite(grams) || grams < 0) return;
    setIngredients((prev) =>
      prev.map((ing, i) =>
        i === index ? scaleFromBaseline(baselines[index], grams) : ing,
      ),
    );
  };

  const removeIngredient = (index: number) => {
    setIngredients((prev) => prev.filter((_, i) => i !== index));
    setBaselines((prev) => prev.filter((_, i) => i !== index));
    setGramsInputs((prev) => prev.filter((_, i) => i !== index));
  };

  const persist = (id: string) => {
    onSave({
      id,
      name: name.trim(),
      ingredients: ingredients.map((ing) => ({ ...ing })),
      finishedGrams,
      portions,
    });
    setReplaceTarget(null);
  };

  const handleSave = () => {
    if (!canSave) return;
    const trimmedName = name.trim();
    const clash = recipes.find(
      (r) => r.name === trimmedName && r.id !== initial?.id,
    );
    if (clash) {
      setReplaceTarget(clash);
      return;
    }
    persist(initial?.id ?? crypto.randomUUID());
  };

  // Raw sum with the measured weight alongside it, shown only when the dish
  // gained or lost weight while it was made.
  const gramsCell = (raw: number, finished: number) =>
    finishedValid && Math.round(raw * 10) !== Math.round(finished * 10) ? (
      <>
        {formatAmount(raw)}{" "}
        <span className="hint">(ferdig {formatAmount(finished)} g)</span>
      </>
    ) : (
      formatAmount(raw)
    );

  const summaryRow = (
    testid: string,
    label: string,
    gramsContent: React.ReactNode,
    amounts: Record<MealMetric, number>,
  ) => (
    <tr className="row-subtotal" data-testid={testid}>
      <td className="td-label">{label}</td>
      <td data-testid={`${testid}-grams`}>{gramsContent}</td>
      {MEAL_METRICS.map((metric) => (
        <td key={metric} data-testid={`${testid}-${metric}`}>
          {formatAmount(amounts[metric])}
        </td>
      ))}
      <td />
    </tr>
  );

  return (
    <Modal open onClose={onClose} data-testid="recipe-editor-modal" size="xl">
      <h2>{initial ? "Rediger oppskrift" : "Ny oppskrift"}</h2>

      <div className="meal-form-name">
        <label htmlFor="recipe-editor-name">Navn</label>
        <input
          id="recipe-editor-name"
          className="input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="recipe-editor-name-input"
        />
      </div>

      <div className="recipe-editor-facts">
        <div className="recipe-editor-field">
          <label htmlFor="recipe-editor-finished">Ferdig vekt</label>
          <div className="num-input">
            <input
              id="recipe-editor-finished"
              type="text"
              inputMode="decimal"
              value={finishedInput}
              onChange={(e) => {
                setFinishedTouched(true);
                setFinishedInput(e.target.value);
              }}
              data-testid="recipe-editor-finished-input"
            />
            <span className="unit">g</span>
          </div>
          <p className="hint">
            Hva retten veier ferdig. Brukes bare til å regne om gram til andel.
          </p>
        </div>
        <div className="recipe-editor-field">
          <label htmlFor="recipe-editor-portions">Porsjoner</label>
          <div className="num-input">
            <input
              id="recipe-editor-portions"
              type="number"
              min={1}
              step={1}
              value={portionsInput}
              onChange={(e) => setPortionsInput(e.target.value)}
              data-testid="recipe-editor-portions-input"
            />
          </div>
          <p className="hint">
            Hvor mange deler retten ble delt i. Med 1 vises ikke «porsjon» som
            enhet.
          </p>
        </div>
      </div>

      {ingredients.length > 0 ? (
        <div className="week-matrix meal-table">
          <table data-testid="recipe-editor-table">
            <thead>
              <tr>
                <th className="th-group">Ingrediens</th>
                <th>g</th>
                {MEAL_METRICS.map((metric) => (
                  <th key={metric}>
                    {metric === "kcal" ? "kcal" : METRIC_LABELS[metric]}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {ingredients.map((ing, index) => (
                <tr
                  // biome-ignore lint/suspicious/noArrayIndexKey: rows are replaced wholesale per estimate or removed in place, never reordered.
                  key={index}
                  data-testid={`recipe-editor-ingredient-row-${index}`}
                >
                  <td className="td-label">{ing.name}</td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      className="ingredient-grams"
                      value={gramsInputs[index] ?? ""}
                      onChange={(e) => changeGrams(index, e.target.value)}
                      aria-label={`Gram ${ing.name}`}
                      data-testid={`recipe-editor-grams-input-${index}`}
                    />
                  </td>
                  {MEAL_METRICS.map((metric) => (
                    <td key={metric}>{formatAmount(ing[metric])}</td>
                  ))}
                  <td className="td-actions">
                    <button
                      type="button"
                      className="icon-btn-delete"
                      onClick={() => removeIngredient(index)}
                      aria-label={`Fjern ${ing.name}`}
                      data-testid={`recipe-editor-delete-ingredient-button-${index}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {summaryRow(
                "recipe-editor-total-row",
                "Hele oppskriften",
                gramsCell(rawGrams, finishedGrams),
                totals,
              )}
              {portionsValid && portions > 1
                ? summaryRow(
                    "recipe-editor-per-portion-row",
                    `Per porsjon (${portions})`,
                    gramsCell(rawGrams / portions, finishedGrams / portions),
                    scaleMetrics(totals, 1 / portions),
                  )
                : null}
              {finishedValid
                ? summaryRow(
                    "recipe-editor-per-100g-row",
                    "Per 100 g ferdig",
                    "100",
                    scaleMetrics(totals, 100 / finishedGrams),
                  )
                : null}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="hint">
          Beskriv retten i meldingsfeltet under, så estimerer KI ingrediensene
          og næringsinnholdet.
        </p>
      )}

      <div className="meal-chat">
        {transcript.length > 0 ? (
          <div
            className="meal-chat-log"
            ref={chatLogRef}
            data-testid="recipe-editor-chat-log"
          >
            {transcript.map((message, index) => (
              <p
                // biome-ignore lint/suspicious/noArrayIndexKey: transcript is append-only.
                key={index}
                className={`chat-msg ${message.role}`}
              >
                {message.text}
              </p>
            ))}
          </div>
        ) : null}
        {chatError ? (
          <p className="field-error" data-testid="recipe-editor-estimate-error">
            {chatError}
          </p>
        ) : null}
        <div className="meal-chat-compose">
          <textarea
            className="meal-chat-input"
            rows={6}
            placeholder={
              ingredients.length === 0
                ? "f.eks. pastasaus med kjøttdeig og tomat, ble 900 g, delte i 4"
                : "skriv en justering…"
            }
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void send();
              }
            }}
            data-testid="recipe-editor-chat-input"
          />
          <button
            type="button"
            className="btn"
            disabled={draft.trim().length === 0 || sending}
            onClick={() => void send()}
            data-testid="recipe-editor-send-button"
          >
            {sending ? "Estimerer…" : "Send"}
          </button>
        </div>
        <p className="hint chat-send-hint">Ctrl + Enter for å sende</p>
      </div>

      <div className="modal-actions">
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          data-testid="recipe-editor-cancel-button"
        >
          Avbryt
        </button>
        <button
          type="button"
          className="btn"
          disabled={!canSave}
          onClick={handleSave}
          data-testid="recipe-editor-save-button"
        >
          Lagre
        </button>
      </div>

      <ConfirmModal
        open={replaceTarget !== null}
        title="Erstatt oppskrift?"
        body={`Oppskriften «${replaceTarget?.name ?? ""}» finnes fra før. Innholdet erstattes med denne. Måltider som allerede er logget beholder sin egen kopi.`}
        confirmLabel="Erstatt"
        onConfirm={() => {
          if (replaceTarget) persist(replaceTarget.id);
        }}
        onCancel={() => setReplaceTarget(null)}
        data-testid="recipe-editor-replace-modal"
      />
    </Modal>
  );
}
